"use client";

import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  styled,
  Divider,
  Box,
  Chip,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { motion, AnimatePresence } from "framer-motion";
import { Roboto } from "next/font/google";
import { useTranslation } from "react-i18next";

const roboto = Roboto({ subsets: ["latin"], weight: ["400", "500", "700"] });

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: "none",
  "&:before": { display: "none" },
  "&:not(:last-child)": { borderBottom: `1px solid ${theme.palette.divider}` },
  background: "transparent",
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  "& .MuiAccordionSummary-content": { margin: theme.spacing(0) },
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: theme.palette.text.secondary,
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0, 0, 2, 0),
}));

const ZaynFaqs = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const faqsRaw = t("zaynFaqs.faqs", { returnObjects: true });
  const faqs = Array.isArray(faqsRaw) ? faqsRaw : [];

  const [expanded, setExpanded] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const INITIAL_FAQ_COUNT = 6;

  const visibleFaqs = showAll ? faqs : faqs.slice(0, INITIAL_FAQ_COUNT);

  const handleChange = (index) => (event, isExpanded) => {
    setExpanded(isExpanded ? index : false);
  };

  return (
    <Box sx={{ direction: isArabic ? "rtl" : "ltr" }}>
      <Container
        maxWidth="md"
        component={motion.div}
        initial="hidden"
        sx={{
          py: { xs: 4, sm: 6, md: 8 },
          mt: { xs: "2%", sm: "2%", md: "1%" },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", md: "center" },
          }}
        >
          <Chip
            label={t("zaynFaqs.chip")}
            sx={{
              backgroundColor: "white",
              border: "1px solid #0206174D",
              mb: { xs: 2, sm: 2.5, md: 3 },
              color: "#647482",
              fontSize: { xs: "12px", sm: "14px", md: "16px" },
              fontWeight: "400",
              textTransform: "capitalize",
              px: { xs: 0.5, sm: 0.7 },
              py: { xs: 1.2, sm: 1.5, md: 2.3 },
              fontFamily: "var(--font-outfit)",
              borderRadius: "40px",
              textAlign: "center",
            }}
          />
        </Box>

        <Typography
          component="h2"
          sx={{
            mb: { xs: 3, sm: 4, md: 6 },
            fontSize: { xs: "1.5rem", sm: "2rem", md: "40px" },
            color: "#0F172A",
            fontFamily: "var(--font-plusjakarta)",
            maxWidth: { xs: "100%", md: 700 },
            textAlign: { xs: isArabic ? "right" : "left", md: "center" },
            mx: { xs: 0, md: "auto" },
            lineHeight: { xs: "1.4", md: "1.3" },
            fontWeight: 600,
          }}
        >
          {t("zaynFaqs.title")}{" "}
          <span style={{ color: "#775CE5" }}>
            {t("zaynFaqs.titleHighlight")}
          </span>
        </Typography>

        <Divider sx={{ mb: ".2" }} />

        <AnimatePresence>
          {visibleFaqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <StyledAccordion
                disableGutters
                expanded={expanded === index}
                onChange={handleChange(index)}
              >
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ flexDirection: isArabic ? "row" : "row" }}
                >
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontFamily: "var(--font-outfit)",
                      fontSize: { xs: "14px", sm: "15px", md: "18px" },
                      lineHeight: { xs: "1.5", sm: "1.6" },
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {faq.question}
                  </Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Typography
                    color="text.secondary"
                    sx={{
                      fontFamily: "var(--font-outfit)",
                      fontSize: { xs: "13px", sm: "15px", md: "18px" },
                      lineHeight: { xs: "1.6", sm: "1.7" },
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </StyledAccordionDetails>
              </StyledAccordion>
            </motion.div>
          ))}
        </AnimatePresence>

        {faqs.length > INITIAL_FAQ_COUNT && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: { xs: 3, sm: 4, md: 5 },
            }}
          >
            <Button
              component={motion.button}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAll(!showAll)}
              sx={{
                bgcolor: "#FFFFFF",
                color: "#5B6B77",
                textTransform: "none",
                px: 3,
                py: 1.5,
                fontFamily: "var(--font-outfit)",
                fontSize: { xs: "0.9rem", md: "1rem" },
                fontWeight: "500",
                borderRadius: "28px",
                border: "1px solid #E2E8F0",
                boxShadow: "none",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  bgcolor: "#FFFFFF",
                  boxShadow: "none",
                  transform: "scale(1.05)",
                },
              }}
            >
              {showAll ? t("zaynFaqs.seeLess") : t("zaynFaqs.seeMore")}
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ZaynFaqs;
